import { h, empty } from '../../../../../src/shared/components/dom.js';
import { Spinner } from '../../../../../src/shared/components/spinner.js';
import { EmptyState } from '../../../../../src/shared/components/empty-state.js';
import { getLists, createList } from '../../api.js';
import { navigate } from '../../router.js';
import { openListNameModal } from '../../components/list-name-modal.js';

/**
 * Lists overview — /library/lists
 *
 * Full-page list of all the user's curated lists, complementing the
 * compact "My lists" section in the sidebar. Each card shows the list
 * name, description, item count, and visibility. Clicking a card opens
 * the list detail page.
 *
 * Unauthenticated users land on a sign-in nudge.
 */
export async function render(_params, { root, isCurrent = () => true }) {
  empty(root);
  root.appendChild(Spinner({ label: 'Loading your lists…' }));

  let lists;
  try {
    lists = await getLists();
    if (!isCurrent()) return false;
  } catch (err) {
    if (!isCurrent()) return false;
    empty(root);
    if (err?.status === 401) {
      root.appendChild(EmptyState({
        title: 'Sign in to see your lists',
        message: 'Lists are per-user. Sign in from the main app to create and see them.',
        action: h('a', { class: 'lib-btn', href: '/' }, 'Back to main app'),
        headingLevel: 'h1',
      }));
      return true;
    }
    root.appendChild(EmptyState({
      title: 'Couldn\'t load lists',
      message: 'Try again in a moment.',
      action: h('a', { class: 'lib-btn', href: '/library' }, '← Back to library'),
      headingLevel: 'h1',
    }));
    return false;
  }

  empty(root);
  root.appendChild(buildScreen(lists, root));
  return true;
}

function buildScreen(lists, root) {
  const screen = h('section', { class: 'lib-lists-screen' });

  // Back link + header
  screen.appendChild(
    h('a', { class: 'lib-game-back', href: '/library' }, '← Library')
  );
  screen.appendChild(
    h('div', { class: 'lib-list-header' },
      h('h1', { class: 'lib-list-title' }, 'My lists'),
      h('button', {
        class: 'lib-btn lib-btn-primary',
        type: 'button',
        onclick: () => promptCreate(root),
      }, '+ New list'),
    )
  );

  if (!lists.length) {
    // No lists yet — guide the user toward either route to create one.
    // The "+ New list" button above works, but also surface the more
    // common flow (create-while-adding from a game tile / detail page)
    // so users with a specific game in mind know where to start.
    screen.appendChild(
      h('div', { class: 'lib-list-empty' },
        h('p', null, 'You haven\'t created any lists yet.'),
        h('p', { class: 'lib-list-empty-hint' },
          'Create one with ',
          h('strong', null, '+ New list'),
          ' above, or browse the library and click the ',
          h('span', { class: 'lib-list-empty-icon' }, '+'),
          ' on a game tile. You can create a list inline when you add your first game.',
        ),
        h('a', { class: 'lib-btn lib-btn-primary', href: '/library' }, 'Browse games →'),
      )
    );
    return screen;
  }

  // Grid of list cards. Intentionally a different layout from the game
  // grid — these are metadata cards, not box-art tiles.
  const grid = h('div', { class: 'lib-lists-grid' });
  for (const list of lists) {
    grid.appendChild(renderListCard(list));
  }
  screen.appendChild(grid);
  return screen;
}

function renderListCard(list) {
  const card = h('a', {
    class: 'lib-list-card',
    href: `/library/list/${list.id}`,
    'aria-label': `Open ${list.name}`,
  },
    h('h2', { class: 'lib-list-card-title' }, list.name),
    list.description
      ? h('p', { class: 'lib-list-card-description' }, truncate(list.description, 140))
      : null,
    h('div', { class: 'lib-list-card-meta' },
      h('span', null, `${list.item_count || 0} ${list.item_count === 1 ? 'game' : 'games'}`),
      h('span', { class: `lib-list-card-visibility${list.kind === 'queue' ? ' lib-list-card-queue' : ''}` },
        list.kind === 'queue' ? 'QUEUE'
          : list.visibility === 'public' ? 'PUBLIC' : 'PRIVATE'),
    ),
  );
  return card;
}

async function promptCreate(root) {
  let error = '';
  let value = '';
  for (;;) {
    const name = await openListNameModal({ title: 'New list', value, error });
    if (!name) return;
    value = name;
    try {
      const created = await createList({ name });
      navigate(`/library/list/${created.id}`);
      return;
    } catch (err) {
      error = err?.body?.error === 'name_taken'
        ? 'You already have a list with that name.'
        : err?.body?.error === 'list_limit_reached'
          ? 'You\'ve hit the list limit. Delete an old one first.'
          : 'Could not create the list.';
      console.error('[lists] create failed:', err);
    }
  }
}

function truncate(s, max) {
  if (!s || s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
